/**
 * The phone live browser's stage: the space BrowserLiveModal gives the live view. The browser is created in this
 * shape (Browserbase fixes a session's size at creation), so a browser the agent starts by itself
 * (AgentSurface's `browser_pane`) and one started from the card get the same size. The modal lays its bars out
 * with these same constants.
 *
 * Portrait: the screen less the safe areas and the two glass bars. Landscape: the bars float over the stage and
 * hide themselves, so the stage is the whole screen less the side safe areas.
 */

/** Top glass bar, below the top safe area (minimize, title and domain, menu). */
export const LIVE_TOP_BAR = 52
/** Bottom glass bar, above the bottom safe area: a status row (36) and an action row (44), padded. */
export const LIVE_BOTTOM_BAR = 108

export interface SafeArea {
    top: number
    bottom: number
    left: number
    right: number
}

export function liveStageSize(viewport: { width: number; height: number }, safe: SafeArea): { width: number; height: number } {
    const width = Math.max(0, Math.round(viewport.width - safe.left - safe.right))
    if (viewport.width > viewport.height) return { width, height: Math.max(0, Math.round(viewport.height)) }
    return { width, height: Math.max(0, Math.round(viewport.height - safe.top - LIVE_TOP_BAR - safe.bottom - LIVE_BOTTOM_BAR)) }
}

/** The device's safe-area insets, read through a hidden probe (env() is only readable from CSS). */
export function measureSafeArea(): SafeArea {
    if (typeof document === 'undefined') return { top: 0, bottom: 0, left: 0, right: 0 }
    const probe = document.createElement('div')
    probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;'
        + 'padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)'
    document.body.appendChild(probe)
    const cs = getComputedStyle(probe)
    const px = (v: string) => parseFloat(v) || 0
    const out = { top: px(cs.paddingTop), bottom: px(cs.paddingBottom), left: px(cs.paddingLeft), right: px(cs.paddingRight) }
    probe.remove()
    return out
}

/** The stage the live mode would give the browser if it opened now (for a browser the agent starts itself). */
export function expectedLiveStage(): { width: number; height: number } | null {
    if (typeof window === 'undefined') return null
    const size = liveStageSize({ width: window.innerWidth, height: window.innerHeight }, measureSafeArea())
    return size.width > 50 && size.height > 50 ? size : null
}
